/**
 * A saved, re-launchable run recipe (backend issue #108, eval PR #113). A
 * preset stores the *inputs* to a run — a selection (case_ids and/or tag_ids)
 * plus its dimensions — not a frozen instance. Each launch re-resolves the
 * selection against the current case/scenario/rubric versions and pins fresh,
 * exactly as a direct run does, which is what makes presets useful for
 * regression tracking across launches.
 *
 * As with the run-create UI, model / prompt_profile / inference_params are
 * server-defaulted and not surfaced here; the editor manages the selection,
 * include-mutants, and trials only.
 */
export type EvalRunPreset = {
  preset_id: string
  name: string
  case_ids: string[]
  tag_ids: string[]
  include_mutants: boolean
  model: string | null
  prompt_profile: string | null
  inference_params: Record<string, unknown>
  trials: number
  tag: string | null
  created_by: string | null
  created_at: string
}

export type EvalRunPresetListResponse = {
  presets: EvalRunPreset[]
  total: number
  limit: number
  offset: number
}

/** Default page size for the preset list (backend caps limit at 200). */
export const PRESETS_PAGE_SIZE = 25

export type PresetFilters = {
  page: number
  pageSize: number
}

export const getDefaultPresetFilters = (): PresetFilters => ({
  page: 1,
  pageSize: PRESETS_PAGE_SIZE,
})

/** Build the list query string: pagination only (backend orders newest-first). */
export const buildPresetsListQuery = (
  filters: PresetFilters,
): URLSearchParams => {
  const query = new URLSearchParams()
  query.set('limit', String(filters.pageSize))
  query.set('offset', String((filters.page - 1) * filters.pageSize))
  return query
}

/** Create payload: the recipe fields the editor manages. */
export type EvalRunPresetCreateInput = {
  name: string
  case_ids: string[]
  tag_ids: string[]
  include_mutants: boolean
  trials: number
}

/** PATCH payload: the same recipe fields, carrying the preset id. */
export type EvalRunPresetUpdateInput = EvalRunPresetCreateInput & {
  preset_id: string
}

/**
 * Per-launch overrides (dimensions only). Selection and name are fixed to the
 * preset; overrides apply to that launch only and never touch the stored
 * preset. The UI surfaces the same dimensions it exposes for a direct run.
 */
export type PresetLaunchOverrides = {
  include_mutants: boolean
  trials: number
}

/** Editor state for the create/edit-preset modal. */
export type PresetForm = {
  name: string
  caseIds: string[]
  tagIds: string[]
  includeMutants: boolean
  trials: number
}

export const emptyPresetForm = (): PresetForm => ({
  name: '',
  caseIds: [],
  tagIds: [],
  includeMutants: false,
  trials: 1,
})

/** Map a stored preset back to editor state (for the edit modal). */
export const presetFormFromPreset = (preset: EvalRunPreset): PresetForm => ({
  name: preset.name,
  caseIds: preset.case_ids,
  tagIds: preset.tag_ids,
  includeMutants: preset.include_mutants,
  trials: preset.trials,
})

/**
 * Whether the selection is empty (no cases and no tags). The backend rejects an
 * empty selection with 422 at launch; we mirror that to keep the submit button
 * inert while editing.
 */
export const isPresetSelectionEmpty = (form: PresetForm): boolean =>
  form.caseIds.length === 0 && form.tagIds.length === 0

/** Pre-flight the backend's name rule: a preset name must be non-empty. */
export const validatePresetName = (name: string): string | null => {
  if (!name.trim()) {
    return 'Name is required.'
  }
  return null
}

export const buildPresetCreateInput = (
  form: PresetForm,
): EvalRunPresetCreateInput => ({
  name: form.name.trim(),
  case_ids: form.caseIds,
  tag_ids: form.tagIds,
  include_mutants: form.includeMutants,
  trials: form.trials,
})

export const buildPresetUpdateInput = (
  preset: EvalRunPreset,
  form: PresetForm,
): EvalRunPresetUpdateInput => ({
  preset_id: preset.preset_id,
  ...buildPresetCreateInput(form),
})

/** Editor state for the launch dialog: the dimensions overridable per launch. */
export type PresetLaunchForm = {
  includeMutants: boolean
  trials: number
}

export const launchFormFromPreset = (
  preset: EvalRunPreset,
): PresetLaunchForm => ({
  includeMutants: preset.include_mutants,
  trials: preset.trials,
})

export const buildLaunchOverrides = (
  form: PresetLaunchForm,
): PresetLaunchOverrides => ({
  include_mutants: form.includeMutants,
  trials: form.trials,
})

/** Human-readable selection summary for a preset row, e.g. "2 case(s), 1 tag(s)". */
export const formatSelectionSummary = (preset: EvalRunPreset): string => {
  const parts: string[] = []
  if (preset.case_ids.length > 0) {
    parts.push(`${preset.case_ids.length} case(s)`)
  }
  if (preset.tag_ids.length > 0) {
    parts.push(`${preset.tag_ids.length} tag(s)`)
  }
  return parts.length > 0 ? parts.join(', ') : 'empty selection'
}
